/**
 * LoginLinkPage
 * Feature: 076-unified-login-tokens
 *
 * Landing page for passwordless login links. User receives a link with a nonce
 * and a PIN in the email. They must enter the PIN to verify ownership,
 * then are logged in automatically.
 */

import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Container, Stack } from '@/components/layout';
import { Heading, Text } from '@/components/atoms';
import { useExchangeLoginTokenGraphQL } from '@/hooks/graphql/useLoginTokensGraphQL';
import logger from '@/utils/logger';

export function LoginLinkPage() {
  const [searchParams] = useSearchParams();
  const [nonce, setNonce] = useState<string | null>(null);
  const [pin, setPin] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { exchangeToken, loading, error } = useExchangeLoginTokenGraphQL();

  // Extract and validate nonce from URL
  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (!tokenParam || !/^[0-9a-f]{64}$/i.test(tokenParam)) {
      setErrorMessage('Invalid or missing token. Please check your link.');
      logger.warn('Auth', 'Invalid login token format', {
        hasToken: !!tokenParam,
        length: tokenParam?.length,
      });
    } else {
      setNonce(tokenParam);
    }
  }, [searchParams]);

  // Handle PIN input (only allow digits, max 6)
  const handlePinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setPin(value);
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    if (!nonce) {
      setErrorMessage('Invalid token');
      return;
    }

    if (!/^[0-9]{6}$/.test(pin)) {
      setErrorMessage('PIN must be 6 digits');
      return;
    }

    try {
      const result = await exchangeToken(nonce, pin);

      if (result) {
        logger.info('Auth', 'Login via link successful, redirecting');
        // Full page reload to ensure all auth state is refreshed
        // (Apollo cache, React context, header avatar, etc.)
        window.location.href = '/';
      }
    } catch (err: unknown) {
      logger.warn('Auth', 'Login link exchange failed', { error: err });
      // Error is handled by the hook
    }
  };

  // Show error state if token is invalid
  if (!nonce && errorMessage) {
    return (
      <Container maxWidth="md">
        <Stack direction="vertical" spacing={6} className="py-12">
          <div className="text-center">
            <Heading level="h1">Invalid Link</Heading>
            <Text className="mt-4 text-destructive">{errorMessage}</Text>
            <Text className="mt-2 text-content-secondary">
              Please request a new login link.
            </Text>
          </div>
        </Stack>
      </Container>
    );
  }

  // Loading state while validating
  if (!nonce) {
    return (
      <Container maxWidth="md">
        <Stack direction="vertical" spacing={6} className="py-12">
          <div className="text-center">
            <Text>Validating link...</Text>
          </div>
        </Stack>
      </Container>
    );
  }

  // Get error message from GraphQL error
  const graphQLError = error?.graphQLErrors?.[0]?.message || error?.message;

  return (
    <Container maxWidth="sm">
      <Stack direction="vertical" spacing={6} className="py-12">
        <div className="text-center">
          <Heading level="h1">Enter PIN to Log In</Heading>
          <Text className="mt-2 text-content-secondary">
            Enter the 6-digit PIN from your email to continue.
          </Text>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* PIN Input */}
          <div className="space-y-2">
            <label
              htmlFor="pin"
              className="block text-sm font-medium text-foreground"
            >
              PIN
            </label>
            <input
              id="pin"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="one-time-code"
              value={pin}
              onChange={handlePinChange}
              placeholder="000000"
              className="w-full px-4 py-3 text-2xl font-mono tracking-widest text-center border border-border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              maxLength={6}
              disabled={loading}
              autoFocus
            />
          </div>

          {/* Error message */}
          {(errorMessage || graphQLError) && (
            <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-lg">
              {errorMessage || graphQLError}
            </div>
          )}

          {/* Submit button */}
          <button
            type="submit"
            disabled={loading || pin.length !== 6}
            className={`w-full py-3 px-4 text-sm font-medium rounded-lg transition-colors ${
              loading || pin.length !== 6
                ? 'bg-muted text-muted-foreground cursor-not-allowed'
                : 'bg-primary text-primary-foreground hover:bg-primary/90'
            }`}
          >
            {loading ? 'Logging in...' : 'Log In'}
          </button>
        </form>

        {/* Help text */}
        <div className="text-center text-sm text-muted-foreground">
          <p>The PIN was sent to your email along with this link.</p>
          <p className="mt-1">
            The link and PIN expire after 1 hour for security.
          </p>
        </div>
      </Stack>
    </Container>
  );
}
